/**
 * PalladiumChart — Chart.js Initializer
 *
 * Lazy-loads the vendored Chart.js (first-party, so privacy/ad blockers can't
 * strip it — a blocked CDN script left charts silently blank), then renders
 * charts from Blazor config. Called by ChartWidget.razor via IJSRuntime.
 * The ONE shared copy lives in Palladium.Client (also used by the Defender and
 * Commerce dashboards). CDN kept as last-resort fallback for stale deploys
 * missing the lib folder — pinned to the same exact version as the local copy.
 */
(function () {
    'use strict';

    var CHART_LOCAL = '/_content/Palladium.Client/js/lib/chartjs/chart.umd.min.js';
    var CHART_CDN = 'https://cdn.jsdelivr.net/npm/chart.js@4.5.1/dist/chart.umd.min.js';
    var _libLoaded = null;
    var _instances = {};

    function loadLib() {
        if (_libLoaded) return _libLoaded;
        _libLoaded = new Promise(function (resolve, reject) {
            if (window.Chart) { resolve(); return; }
            var s = document.createElement('script');
            s.src = CHART_LOCAL;
            s.onload = resolve;
            s.onerror = function () {
                var f = document.createElement('script');
                f.src = CHART_CDN;
                f.onload = resolve;
                f.onerror = function () { reject(new Error('Failed to load Chart.js')); };
                document.head.appendChild(f);
            };
            document.head.appendChild(s);
        });
        return _libLoaded;
    }

    window.PalladiumChart = {
        render: async function (canvasId, config) {
            await loadLib();

            // Destroy previous instance
            if (_instances[canvasId]) {
                _instances[canvasId].destroy();
                delete _instances[canvasId];
            }

            var canvas = document.getElementById(canvasId);
            if (!canvas) return;

            var ctx = canvas.getContext('2d');

            var chartConfig = {
                type: config.type || 'bar',
                data: {
                    labels: config.labels || [],
                    datasets: (config.datasets || []).map(function (ds) {
                        return {
                            label: ds.label || '',
                            data: ds.data || [],
                            backgroundColor: ds.backgroundColor,
                            borderColor: ds.borderColor,
                            borderWidth: ds.borderWidth || 2,
                            fill: ds.fill || false,
                            tension: ds.tension || 0,
                        };
                    }),
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: config.indexAxis || 'x',
                    plugins: {
                        legend: {
                            display: config.showLegend !== false,
                        },
                        title: {
                            display: !!config.showTitle,
                            text: config.title || '',
                            font: { size: 16 },
                        },
                    },
                    scales: {},
                },
            };

            // Stacked axes (only for bar/line)
            var stackableTypes = ['bar', 'line'];
            if (config.stacked && stackableTypes.indexOf(config.type) !== -1) {
                chartConfig.options.scales.x = { stacked: true };
                chartConfig.options.scales.y = { stacked: true };
            }

            // No scales for pie/doughnut/polarArea/radar
            var noScaleTypes = ['pie', 'doughnut', 'polarArea', 'radar'];
            if (noScaleTypes.indexOf(config.type) !== -1) {
                delete chartConfig.options.scales;
                delete chartConfig.options.indexAxis;
            }

            // Set container height
            var container = canvas.parentElement;
            if (container && config.height) {
                container.style.height = config.height + 'px';
            }

            _instances[canvasId] = new Chart(ctx, chartConfig);
        },

        destroy: function (canvasId) {
            if (_instances[canvasId]) {
                _instances[canvasId].destroy();
                delete _instances[canvasId];
            }
        },

        destroyAll: function () {
            Object.keys(_instances).forEach(function (id) {
                _instances[id].destroy();
            });
            _instances = {};
        },
    };
})();
